import { Modal, Stack, Text, Table, Checkbox, Button, Group, Alert } from '@mantine/core';
import { useState } from 'react';
import { IconShieldCheck, IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { supabase } from '@/lib/supabase';
import type { OrganizationMember } from '@/types/models';
import type { UserPermissions, PagePermissions } from '@/hooks/usePermissions';

interface UserPermissionsModalProps {
  opened: boolean;
  onClose: () => void;
  member: OrganizationMember | null;
  onSuccess: () => void;
}

const PAGES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'assets', label: 'Assets' },
  { id: 'audits', label: 'Audits' },
  { id: 'reports', label: 'Reports' },
  { id: 'people', label: 'People' },
  { id: 'locations', label: 'Locations' },
  { id: 'categories', label: 'Categories' },
  { id: 'departments', label: 'Departments' },
  { id: 'custom_fields', label: 'Custom Fields' },
  { id: 'settings', label: 'Settings' },
];

const DEFAULT_PAGE_PERMISSIONS: PagePermissions = {
  view: false,
  write: false,
  delete: false,
};

export function UserPermissionsModal({ opened, onClose, member, onSuccess }: UserPermissionsModalProps) {
  const [permissions, setPermissions] = useState<UserPermissions>(() => {
    if (!member) return {};
    return (member.permissions as UserPermissions) || {};
  });
  const [saving, setSaving] = useState(false);

  const getPagePermissions = (pageId: string): PagePermissions => {
    return permissions[pageId] || DEFAULT_PAGE_PERMISSIONS;
  };

  const updatePagePermission = (pageId: string, field: keyof PagePermissions, value: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [pageId]: {
        ...getPagePermissions(pageId),
        [field]: value,
        // If disabling view, disable all other permissions
        ...(field === 'view' && !value ? {
          write: false,
          delete: false,
        } : {}),
      },
    }));
  };

  const handleSave = async () => {
    if (!member) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ permissions })
        .eq('id', member.id);

      if (error) throw error;

      notifications.show({
        title: 'Permissions updated',
        message: `Permissions for ${member.profile?.email || 'user'} have been updated`,
        color: 'green',
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update permissions',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!member) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconShieldCheck size={24} />
          <div>
            <Text size="lg" fw={600}>Manage Permissions</Text>
            <Text size="sm" c="dimmed">{member.profile?.email}</Text>
          </div>
        </Group>
      }
      size="xl"
    >
      <Stack gap="md">
        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          <Text size="sm">
            Configure what pages this user can access and what actions they can perform.
            Unchecking "View" will hide the page from their navigation.
          </Text>
        </Alert>

        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Page</Table.Th>
              <Table.Th style={{ textAlign: 'center', width: '100px' }}>View</Table.Th>
              <Table.Th style={{ textAlign: 'center', width: '120px' }}>Create/Edit</Table.Th>
              <Table.Th style={{ textAlign: 'center', width: '100px' }}>Delete</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {PAGES.map((page) => {
              const perms = getPagePermissions(page.id);
              return (
                <Table.Tr key={page.id}>
                  <Table.Td>
                    <Text fw={500}>{page.label}</Text>
                  </Table.Td>
                  <Table.Td>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Checkbox
                        checked={perms.view}
                        onChange={(e) => updatePagePermission(page.id, 'view', e.currentTarget.checked)}
                      />
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Checkbox
                        checked={perms.write}
                        disabled={!perms.view}
                        onChange={(e) => updatePagePermission(page.id, 'write', e.currentTarget.checked)}
                      />
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Checkbox
                        checked={perms.delete}
                        disabled={!perms.view}
                        onChange={(e) => updatePagePermission(page.id, 'delete', e.currentTarget.checked)}
                      />
                    </div>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} leftSection={<IconShieldCheck size={16} />}>
            Save Permissions
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
